/**
 * What a screen asks of a channel.
 *
 * ## Two layers, and which wins
 *
 * The CHANNEL says what is permitted (`features.pinning`, `features.mentions`,
 * `features.attachments`, `features.maxMessageLength`) — those flags come from the
 * channel type's defaults with the channel's own overrides applied, and the server
 * enforces them. The SURFACE says what this particular screen wants.
 *
 * A surface may only narrow: `pins: true` on a channel whose type has pinning disabled
 * stays off, because the pin route would answer 403. That rule lives once, in
 * {@link resolveChannelSurface}, so no component re-derives it.
 *
 * ## Everything is optional
 *
 * `ChannelView` is usable as `<ChannelView cid={cid} />`. The defaults are the shape of
 * a full conversation — thread, day separators, unread marker, composer, realtime — and
 * a caller narrows from there. That direction matters: a surface that forgets a config
 * key gets the complete component, not a broken one.
 */

import type { Extensions } from "@tiptap/core";
import type { ComponentType, ReactNode } from "react";

import type {
  ChatComposerApi,
  ChatComposerValues,
} from "@/features/shared/chat-composer";
import type {
  Channel,
  ChannelFeatures,
  ChannelMessage,
  ChannelMessageBlock,
  ChannelPromptBlock,
  CreateChannelMessageInput,
} from "../_api/channel-endpoints";
import type { ReadReceiptMode } from "../_libs/read-receipt-visibility";

/** What a block renderer is handed. */
export type ChannelBlockContext = {
  block: ChannelMessageBlock;
  message: ChannelMessage;
  channel: Channel;
  /**
   * What the containing bubble wants on a block card's shell — the spacing between cards
   * and, where it differs from the shell's default, the background.
   *
   * The card decides what it looks like; the bubble decides how it sits. `ChannelMessageItem`
   * stacks its children with no gap, so it asks for `mt-3`; `ConversationMessageItem` has its
   * own `gap-1` and a tinted bubble, so it asks for `mt-0` and the translucent
   * `bg-bg-light-blur`. Apply it LAST in the card's `cn(...)` so it wins the conflict.
   *
   * A renderer that lays its own cards out (the comment tab's report list, which spaces
   * them with `gap-3`) overrides the margin again: `cn(cardClassName, "mt-0")`.
   */
  cardClassName?: string;
};

/** What a prompt-block renderer is handed. */
export type ChannelPromptContext = {
  prompt: ChannelPromptBlock;
  channel: Channel;
};

/** What a message-action builder is handed. */
export type ChannelMessageContext = {
  message: ChannelMessage;
  channel: Channel;
  /** True when the viewer wrote the message. */
  isOwnMessage: boolean;
};

/**
 * Whether one action is offered on one message.
 *
 * A predicate rather than a boolean because the server's own rule is per-message and
 * differs per channel type — comments are ADMIN-or-author, the homeowner thread is
 * ADMIN-only — so a surface that could only say "edit: true" would paint actions that
 * answer 403. The gate is advisory UI; the server still decides.
 */
export type ChannelMessageActionGate =
  | boolean
  | ((context: ChannelMessageContext) => boolean);

export type ChannelMessageActionsConfig = {
  /**
   * Deep-link builder for the "Copy link" action. Omit to hide the action — the URL
   * shape is the surface's knowledge (`/orders/:orderId/messages/:oldId`), not the
   * channel's.
   */
  copyLink?: (context: ChannelMessageContext) => string;
  /**
   * "Quote reply": quote this message in the composer, so the next post carries a "Reply to
   * <author>" card. Default on wherever the view has a composer — any member who can post
   * may quote any message of the conversation, on every channel type (ENG-4137).
   */
  reply?: ChannelMessageActionGate;
  /** Edit in place. Mirror the server's rule (ADMIN, or the author on notes). */
  edit?: ChannelMessageActionGate;
  delete?: ChannelMessageActionGate;
  /**
   * Copy for the delete confirmation dialog. Defaults to the "message" wording; a
   * surface that deletes something else (a comment, a note) overrides the noun here.
   */
  deleteConfirm?: { title?: string; description?: string };
  /**
   * Copy for the unpin confirmation dialog. Defaults to the "message" wording; a surface
   * that pins something else (a comment, a note) overrides the noun here.
   */
  unpinConfirm?: { title?: string; description?: string };
  /** Pin/unpin. Requires `features.pinning`. */
  pin?: boolean;
  /** Extra items appended to the hover action bar. */
  extra?: (context: ChannelMessageContext) => ChannelMessageAction[];
  /**
   * Open this message's thread. When set, a persistent "{n} replies" affordance is shown
   * under any root that has replies (`replyCount > 0`) — the comment list's way back into
   * a thread, which the hover-only "Reply in thread" action alone did not surface.
   */
  onOpenThread?: (message: ChannelMessage) => void;
};

/**
 * An action in the bubble's "More" menu — the same shape as a bar action plus the one
 * thing a menu row can say that an icon button cannot.
 */
export type ChannelMessageMenuItem = ChannelMessageAction & {
  variant?: "default" | "destructive";
};

/**
 * Where each of a message's actions goes: an icon in the bar, or a row in "More".
 *
 * The split is decided by the policy that builds the list, not by how many actions there
 * happen to be. The bar used to take one flat list and slice it at a limit, which meant
 * the same action moved between the bar and the menu as a sibling appeared or dropped out
 * — Pin landing in the bar on a comment the viewer could not edit, say. A reader learns
 * one position per control, so each one has a fixed home.
 *
 * `bar` is drawn left to right AFTER the add-reaction button, which belongs to the bar
 * itself rather than to any policy. `menu` is drawn top to bottom, destructive rows last
 * under a rule (`channel-message-more-menu.tsx`). An empty `menu` draws no "More" at all.
 */
export type ChannelMessageActionGroups = {
  bar: ChannelMessageMenuItem[];
  menu: ChannelMessageMenuItem[];
};

/** Resolves a gate against one message. */
export function isMessageActionAllowed(
  gate: ChannelMessageActionGate | undefined,
  context: ChannelMessageContext,
): boolean {
  if (typeof gate === "function") return gate(context);
  return gate === true;
}

export type ChannelMessageAction = {
  title: string;
  icon: React.ElementType;
  onClick: (event: unknown) => void;
};

/**
 * What every message renderer is handed — the contract a UI variant implements.
 *
 * `ChannelMessageItem` is one implementation of it; `ConversationMessageItem`
 * (`@/features/shared/conversation`) is the other. Nothing here is UI-specific, which is
 * what lets the two swap without the list, the views or any caller knowing which is in play.
 */
export type ChannelMessageRenderProps = {
  cid: string;
  channel: Channel;
  message: ChannelMessage;
  viewerId: string | undefined;
  isUnread: boolean;
  /** Deep-link / pinned-jump scroll target flash. The just-arrived flash is computed internally. */
  isHighlighted?: boolean;
  showReadReceipts: boolean;
  showPinnedBadge: boolean;
  /**
   * Whether this message offers anything to act on: the hover action bar and, in the
   * conversation UI, the reaction cluster it shares a row with. A thread's parent message
   * is shown as CONTEXT above the replies, so it renders with this off — the body stays,
   * the affordances go. Defaults to on.
   */
  showActions?: boolean;
  actionsConfig: ChannelMessageActionsConfig;
  renderBlock?: (context: ChannelBlockContext) => ReactNode;
  renderAvatar?: (context: { userId: string }) => ReactNode;
  renderUserName?: (context: { userId: string }) => ReactNode;
  onEdit?: (message: ChannelMessage) => void;
  onDelete?: (message: ChannelMessage) => void;
  onTogglePin?: (message: ChannelMessage) => void;
  /** Quote the message in the composer. Absent where there is no composer to quote into. */
  onReply?: (message: ChannelMessage) => void;
  /** Scroll to the message a reply quotes. Absent, the quote card is not clickable. */
  onOpenQuotedMessage?: (messageId: string) => void;
};

/**
 * A drop-in replacement for the default bubble. `undefined` keeps `ChannelMessageItem`.
 *
 * A COMPONENT type, not a render function, on purpose: the list keys each bubble with
 * `id:focusNonce` so that re-requesting the same message remounts only that row and
 * restarts its one-shot highlight animation. A render function would erase that key.
 */
export type ChannelMessageRenderer = ComponentType<ChannelMessageRenderProps>;

/** Replaces `LineAndDate` at the head of each day group. `undefined` keeps it. */
export type ChannelDaySeparatorRenderer = ComponentType<{ date: string }>;

/**
 * The "New" marker drawn at the unread boundary. `undefined` draws no marker at all, which
 * is what every surface did before the marker existed. Where it goes is the list's
 * decision (see `_hooks/use-new-message-divider`); this only supplies the chrome.
 */
export type ChannelNewDividerRenderer = ComponentType;

/**
 * A composer decoration.
 *
 * The function form is handed the live composer, because every real add-on needs it: the
 * office upload trigger clicks a ref the composer owns, Birdie tips write into the
 * editor, and the technician warning re-submits after its dialog is confirmed. A plain
 * `ReactNode` stays legal for decorations that need none of that.
 */
export type ChannelComposerSlot =
  | ReactNode
  | ((composer: ChatComposerApi) => ReactNode);

/** What one send carries beyond its body. See {@link ChannelComposerConfig.onSendStart}. */
export type ChannelSendContext = {
  /** Request headers for this send only. They never replace the client's own. */
  headers?: Record<string, string>;
  /**
   * The send failed, whether or not the box puts its text back: nothing was posted,
   * so undo what counted the message as sent. Runs before `onRestored`.
   */
  onFailed?: () => void;
  /**
   * The send failed and the box put its text back: take back what the send took
   * with it, since the text it belongs to is in the box again.
   */
  onRestored?: () => void;
};

export type ChannelComposerConfig = {
  placeholder?: string;
  /** Send-button label. Default "Send" (comments say "Post"). */
  sendLabel?: string;
  sendTooltip?: string;
  /** Voice notes. */
  audio?: boolean;
  /**
   * Mention support. The channel must allow mentions; the SUGGESTION source is
   * surface-owned (who is mentionable differs per screen), so the caller supplies the
   * configured Tiptap extension rather than a boolean.
   */
  mentions?: Extensions;
  /** OS drag-and-drop onto the box. Default `true`. */
  fileDrop?: boolean;
  /** Rendered first in the action bar (upload trigger, visit actions). */
  leadingActions?: ChannelComposerSlot;
  /**
   * Rendered first in the action bar of the EDIT-in-place composer. Separate from
   * `leadingActions` because that slot also carries non-upload decorations (visit
   * quick actions) that make no sense while editing an existing message.
   */
  editLeadingActions?: ChannelComposerSlot;
  /** Rendered last, before send (AI compose). */
  trailingActions?: ChannelComposerSlot;
  /** Above the box (Birdie tips). */
  header?: ChannelComposerSlot;
  /** Below the box. */
  footer?: ChannelComposerSlot;
  /**
   * Intercepts a user-initiated send, handed what the box holds. Return `false` to stop
   * it — the interceptor then owns what follows: saying why (a blank left to fill in), or
   * re-submitting (a confirmation dialog calling `composer.submit()`).
   */
  onBeforeSend?: (values: ChatComposerValues) => boolean;
  /**
   * Runs once per send, at the press — the moment the text leaves the box, which the
   * composer empties then — and what it returns rides with that send only. For state
   * that belongs to the text but lives outside the box: the Smart Action a drafted
   * message came from.
   */
  onSendStart?: () => ChannelSendContext | undefined;
  /**
   * The failed-send toast's description, for a new message and an edit alike: the
   * server's reason for a refusal the person can fix.
   */
  sendErrorDescription?: (error: unknown) => string | undefined;
  /**
   * Maps composer values to the create body. Default: `{ comment, uploads }`.
   * Override to attach a visit scenario.
   */
  buildInput?: (values: ChatComposerValues) => CreateChannelMessageInput;
};

export type ChannelSurfaceConfig = {
  /** Pinned strip above the thread + pin/unpin in the action bar. Default `true`. */
  pins?: boolean;
  /**
   * Which bubbles wear the delivered/read mark and its "Seen by" card — every bubble
   * (`all`), only the viewer's own (`own`), or none (`off`). Default `all`. A channel type
   * without read events resolves to `off` whatever is asked.
   */
  readReceipts?: ReadReceiptMode;
  /** Advance the read cursor once the thread is on screen. Default `true`. */
  markReadOnView?: boolean;
  /** How long the thread must stay mounted before the cursor advances. Default 3000. */
  markReadDelayMs?: number;
  /**
   * Ask the channel row for its inbox projection too (`useChannel`'s `metadata`).
   *
   * For a surface that renders a conversation header from that projection: the header
   * and this view then read ONE query instead of two entries against the same channel.
   * Default `false` — a surface that only needs the feature flags should not make the
   * server hydrate an inbox row it will not draw.
   */
  includeMetadata?: boolean;
  /** Per-bubble unread dot. Default `true`. */
  unreadDots?: boolean;
  /** Bar listing prompts still awaiting an answer. Default `false`. */
  promptBlocks?: boolean;
  /** Day separator lines. Default `true`. */
  daySeparators?: boolean;
  /**
   * Thread layout. `"chat"` (default) pins newest to the bottom with the composer below
   * (office). `"classic"` puts the composer on top with newest-first messages flowing down
   * in natural height (the member order thread's historical layout).
   */
  layout?: "chat" | "classic";
  /** Broadcast + render "X is typing". Requires `features.typingEvents`. Default `true`. */
  typing?: boolean;
  /**
   * Subscribe to the conversation's realtime channel (the order or home it hangs off)
   * and refresh on its events. Default `true`.
   */
  realtime?: boolean;
  pageSize?: number;
  /**
   * Show only conversation roots, hiding replies that belong to a thread view. Default
   * `false` — the endpoint returns replies inline, which is right for a surface with no
   * threads and wrong for one that has them.
   */
  rootsOnly?: boolean;
  messageActions?: ChannelMessageActionsConfig;
  /** `false` renders a read-only thread with no composer at all. */
  composer?: ChannelComposerConfig | false;
  /**
   * Renders a structured block (a visit card, a shared-availability list).
   * Return `null`/`undefined` to fall through to the slice's default renderer, which
   * covers the channel-agnostic block types only — visit cards are order-domain UI and
   * belong to the surface.
   */
  renderBlock?: (context: ChannelBlockContext) => ReactNode;
  /** Renders one actionable prompt. Required when `promptBlocks` is on. */
  renderPromptBlock?: (context: ChannelPromptContext) => ReactNode;
  /**
   * Replaces the author avatar. The default is presence-free; the office surface passes
   * its online-dot avatar here rather than making every channel depend on the
   * order-scoped presence query.
   */
  renderAvatar?: (context: { userId: string }) => ReactNode;
  /** Replaces the author name (e.g. to append a place role). */
  renderUserName?: (context: { userId: string }) => ReactNode;
  /** Shown when the thread has no messages. */
  emptyState?: ReactNode;
  /** Above the thread, inside the scroll container's sibling slot. */
  header?: ReactNode;
  /**
   * Between the thread and the composer: a notice that stands in for the composer (a
   * closed order). It sits in the gutter box that lines it up with the thread, and that
   * box clips — content meant to hang over the thread goes in `floating`.
   */
  footer?: ReactNode;
  /**
   * Floats over the thread's newest edge, in the jump-to-newest button's zero-height
   * layer: above the composer in the chat layout, below it in `classic`. The content
   * positions itself against that edge (the office tone detector hangs its button
   * upward); the layer sits outside the gutter boxes, so nothing in it is clipped.
   */
  floating?: ReactNode;
  className?: string;
  listClassName?: string;
  /**
   * The content column INSIDE the scroller, and the footer and composer below it. Distinct
   * from `listClassName`, which lands on the scroll container itself — a cap there would drag
   * the scrollbar in off the panel's edge, which is the whole thing this key exists to avoid.
   * Merged onto a box that is already `w-full mx-auto`, so the expected value is a bare `max-w-*`.
   */
  contentClassName?: string;
};

/** Every key filled — what the components consume. */
export type ResolvedChannelSurface = Required<
  Pick<
    ChannelSurfaceConfig,
    | "pins"
    | "readReceipts"
    | "markReadOnView"
    | "markReadDelayMs"
    | "unreadDots"
    | "promptBlocks"
    | "daySeparators"
    | "layout"
    | "typing"
    | "realtime"
    | "pageSize"
    | "rootsOnly"
  >
> &
  Pick<
    ChannelSurfaceConfig,
    | "renderBlock"
    | "renderPromptBlock"
    | "renderAvatar"
    | "renderUserName"
    | "emptyState"
    | "header"
    | "footer"
    | "floating"
    | "className"
    | "listClassName"
    | "contentClassName"
  > & {
    /** `false` means "no composer at all"; an object is always fully present. */
    composer: ChannelComposerConfig | false;
    messageActions: ChannelMessageActionsConfig;
    /** Composer capabilities after the channel's own flags are applied. */
    attachments: boolean;
    mentions: Extensions | undefined;
    maxCharacters: number | undefined;
  };

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_MARK_READ_DELAY_MS = 3000;

/**
 * Folds the surface's wishes against the channel's permissions.
 *
 * Pure — `features` is the only "server" input, and it arrives as data. Both a missing
 * config key and a missing channel produce a sane full-featured surface, so the
 * component renders the same shape while the channel row is still loading.
 */
export function resolveChannelSurface(
  config: ChannelSurfaceConfig | undefined,
  features: ChannelFeatures | undefined,
): ResolvedChannelSurface {
  const surface = config ?? {};
  const canPin = features?.pinning ?? false;
  const canMention = features?.mentions ?? false;
  const canAttach = features?.attachments ?? true;
  const canType = features?.typingEvents ?? true;

  const composer =
    surface.composer === false ? false : (surface.composer ?? {});

  return {
    pins: (surface.pins ?? true) && canPin,
    readReceipts:
      (features?.readEvents ?? true) ? (surface.readReceipts ?? "all") : "off",
    markReadOnView: surface.markReadOnView ?? true,
    markReadDelayMs: surface.markReadDelayMs ?? DEFAULT_MARK_READ_DELAY_MS,
    unreadDots: surface.unreadDots ?? true,
    promptBlocks: surface.promptBlocks ?? false,
    daySeparators: surface.daySeparators ?? true,
    layout: surface.layout ?? "chat",
    typing: (surface.typing ?? true) && canType,
    realtime: surface.realtime ?? true,
    pageSize: surface.pageSize ?? DEFAULT_PAGE_SIZE,
    rootsOnly: surface.rootsOnly ?? false,
    messageActions: {
      ...surface.messageActions,
      pin: (surface.messageActions?.pin ?? true) && canPin,
      // Quoting writes into the composer, so there is nothing to offer without one.
      reply:
        composer === false ? false : (surface.messageActions?.reply ?? true),
    },
    composer,
    renderBlock: surface.renderBlock,
    renderPromptBlock: surface.renderPromptBlock,
    renderAvatar: surface.renderAvatar,
    renderUserName: surface.renderUserName,
    emptyState: surface.emptyState,
    header: surface.header,
    footer: surface.footer,
    floating: surface.floating,
    className: surface.className,
    listClassName: surface.listClassName,
    contentClassName: surface.contentClassName,
    attachments: canAttach,
    mentions: composer === false || !canMention ? undefined : composer.mentions,
    maxCharacters: features?.maxMessageLength,
  };
}
